/* eslint-disable */
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Autocomplete,
	TextField,
	Chip,
	CircularProgress,
	IconButton,
	Tooltip,
} from '@mui/material';
import { RefreshCcw, X } from 'lucide-react';
import { BackdropLoader } from '../../../Components/Loaders';
import VariantTable from '../../Food Products/VariantPrice/VariantTable';
import { notificationServices } from '../../../Services';
import departmentServices from '../../../Services/DepartmentServices/departmentServices';
import userRolesAndPermissions from '../../../Services/userRolesAndPermissions.services';
import { useAuth } from '@/Components/Auth/Login/auth';

// ─── Table Config ────────────────────────────────────────────────────────────

const VARIANT_COLUMNS = [
	{ key: 'id', title: 'ID', width: 80 },
	{ key: 'storeName', title: 'Store', width: 200 },
	{ key: 'roleNames', title: 'Departments', width: 300 },
	{ key: 'createdBy', title: 'Created By', width: 180 },
	{ key: 'status', title: 'Status', width: 140 },
	{ key: 'createdAt', title: 'Created At', width: 180 },
];

// ─── Start Audit Modal ────────────────────────────────────────────────────────

function StartAuditModal({ open, onClose, onConfirm, storesList, departmentsList, departmentsLoading, userId }) {
	const [selectedStores, setSelectedStores] = useState(null);
	const [selectedDepartments, setSelectedDepartments] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const handleClose = () => {
		setSelectedStores(null);
		setSelectedDepartments(null);
		onClose();
	};

	const handleSubmit = async () => {
		if (!selectedStores) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select at least one store',
			});
			return;
		}
		if (!selectedDepartments) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select at least one department',
			});
			return;
		}
		setSubmitting(true);
		await onConfirm({
			storeIds: [selectedStores.id],
			roleIds: [selectedDepartments.id],
			userId,
		});
		setSubmitting(false);
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='sm'
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
					boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
				},
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pb: 2,
					fontWeight: 600,
					fontSize: 20,
					background: '#fff',
					color: 'rgba(0, 0, 0, 0.87)',
					borderBottom: '1px solid #e2e8f0',
				}}
			>
				Start Department Audit
				<IconButton onClick={handleClose} size='small' sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
					<X size={20} />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ pt: 6, pb: 1, mt: 2 }}>
				{/* Store selector */}
				<Autocomplete
					options={storesList ?? []}
					getOptionLabel={(opt) => opt?.name ?? ''}
					value={selectedStores}
					onChange={(_, val) => setSelectedStores(val)}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => (
							<Chip
								key={option.id}
								label={option.name}
								size='small'
								color='primary'
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label='Select Stores *'
							placeholder='Search stores…'
							variant='outlined'
							size='small'
						/>
					)}
					sx={{ mb: 3 }}
				/>

				{/* Department selector */}
				<Autocomplete
					options={departmentsList}
					getOptionLabel={(opt) => opt?.userRole ?? ''}
					value={selectedDepartments}
					onChange={(_, val) => setSelectedDepartments(val)}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					loading={departmentsLoading}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => (
							<Chip
								key={option.id}
								label={option.userRole}
								size='small'
								color='secondary'
								variant='outlined'
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label='Select Departments *'
							placeholder='Search departments…'
							variant='outlined'
							size='small'
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{departmentsLoading ? <CircularProgress size={16} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
					sx={{ mb: 2 }}
				/>

				{/* User ID (read-only info) */}
				<TextField
					label='User ID'
					value={userId ?? '-'}
					size='small'
					fullWidth
					disabled
					variant='outlined'
					helperText='Logged-in user will be associated with this audit'
				/>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
				<Button
					onClick={handleClose}
					variant='outlined'
					color='inherit'
					disabled={submitting}
					sx={{ borderRadius: 1.5, px: 3 }}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant='contained'
					disabled={submitting}
					sx={{
						background: '#facc15',
						'&:hover': { background: '#eab308' },
						color: '#000',
						borderRadius: 1.5,
						px: 3,
						textTransform: 'none',
						fontWeight: 500,
					}}
					startIcon={
						submitting ? <CircularProgress size={16} color='inherit' /> : null
					}
				>
					{submitting ? 'Starting…' : 'Start Audit'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function MyDepartmentsAudit({ storesList }) {
	const { userConfig = {} } = useAuth();
	const { userDetails = {} } = userConfig || {};
	const level = userDetails?.level;
	const userId = userDetails?.id;
	const [isLoading, setIsLoading] = useState(false);
	const [auditList, setAuditList] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [departmentsData, setDepartmentsData] = useState([]);
	const [usersList, setUsersList] = useState([]);
	const [isDeptsLoading, setIsDeptsLoading] = useState(false);

	// ── Fetch users ───────────────────────────────────────────────────────────

	const fetchUsers = async () => {
		if (usersList.length > 0) return;
		try {
			const response = await userRolesAndPermissions.getUsersList();
			if (response?.statusCode === 200) {
				setUsersList(response?.data ?? []);
			}
		} catch (error) {
			console.error('Failed to fetch users:', error);
		}
	};

	// ── Fetch departments ─────────────────────────────────────────────────────

	const fetchDepartments = async () => {
		if (departmentsData.length > 0) return;
		setIsDeptsLoading(true);
		try {
			const response = await departmentServices.fetchDepartmentsList();
			if (response?.statusCode === 200) {
				setDepartmentsData(response?.data ?? []);
			}
		} catch (error) {
			console.error('Failed to fetch departments:', error);
		} finally {
			setIsDeptsLoading(false);
		}
	};

	// ── Fetch audit list ──────────────────────────────────────────────────────

	const fetchAudits = async () => {
		setIsLoading(true);
		try {
			const response = await departmentServices.fetchDepartmentsAuditList();
			if (response?.statusCode === 200) {
				const formatted = (response?.data ?? []).map((item) => ({
					...item,
					createdAt: item?.createdAt
						? moment(item.createdAt).format('YYYY-MM-DD hh:mm A')
						: '-',
				}));
				setAuditList(formatted);
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message ?? 'Audit list fetched successfully',
				});
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message ?? 'Failed to fetch audits',
				});
			}
		} catch (error) {
			console.error(error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'An error occurred while fetching audits',
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchAudits();
		fetchDepartments();
		fetchUsers();
	}, []);

	// ── Start audit ───────────────────────────────────────────────────────────

	const handleStartAudit = async ({ storeIds, roleIds, userId }) => {
		try {
			const req = { storeIds, roleIds, userId };
			const response = await departmentServices.startDepartmentsAudit(req);
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message ?? 'Audit started successfully',
				});
				fetchAudits();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message ?? 'Failed to start audit',
				});
			}
		} catch (error) {
			console.error(error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'An error occurred while starting the audit',
			});
		}
	};

	// ── Special cells ─────────────────────────────────────────────────────────

	const customRowData = {
		id: (obj) => (
			<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
				{obj?.id ?? '-'}
			</div>
		),
		storeName: (obj) => {
			const store = storesList?.find((s) => s.id === obj?.storeId);
			return (
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					{store?.name ?? obj?.storeId ?? '-'}
				</div>
			);
		},
		createdAt: (obj) => (
			<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
				{obj?.createdAt ?? '-'}
			</div>
		),
		createdBy: (obj) => {
			const user = usersList?.find((u) => u.id === obj?.createdBy);
			return (
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					{user?.displayName ?? user?.username ?? obj?.createdBy ?? '-'}
				</div>
			);
		},
		status: (obj) => {
			const statusColors = {
				ACTIVE: { bg: '#dcfce7', color: '#166534' },
				COMPLETED: { bg: '#eff6ff', color: '#1e40af' },
				CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
				SCHEDULED: { bg: '#fef9c3', color: '#713f12' },
			};
			const status = obj?.status;
			const style = statusColors[status] ?? { bg: '#f3f4f6', color: '#374151' };
			return (
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					{!status ? (
						'-'
					) : (
						<span
							style={{
								background: style.bg,
								color: style.color,
								borderRadius: 12,
								padding: '3px 10px',
								fontWeight: 600,
								fontSize: 12,
							}}
						>
							{status}
						</span>
					)}
				</div>
			);
		},
		roleNames: (obj) => {
			const dept = departmentsData?.find((d) => d.id === obj?.roleId);
			return (
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
					{dept?.userRole ?? obj?.roleId ?? '-'}
				</div>
			);
		},
	};

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div style={{ padding: '16px' }}>
			<BackdropLoader showProgress isLoading={isLoading} />

			<StartAuditModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onConfirm={handleStartAudit}
				storesList={storesList}
				departmentsList={departmentsData}
				departmentsLoading={isDeptsLoading}
				userId={userId}
			/>

			{/* Header */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: 16,
				}}
			>
				<span style={{ fontWeight: 700, fontSize: 18 }}>
					Department Audits
				</span>
				<div style={{ display: 'flex', gap: 8 }}>
					<Tooltip title='Refresh list'>
						<IconButton
							size='small'
							onClick={fetchAudits}
							disabled={isLoading}
						>
							<RefreshCcw size={18} />
						</IconButton>
					</Tooltip>
					{level !== 1 && (
						<Button
							variant='contained'
							onClick={() => setModalOpen(true)}
							sx={{
								background: '#facc15',
								'&:hover': {
									background: '#eab308',
								},
								color: '#000',
								borderRadius: 2,
								textTransform: 'none',
								fontWeight: 500,
							}}
						>
							+ Start Department Audit
						</Button>
					)}
				</div>
			</div>

			{/* List */}
			<div className='h-[calc(100vh-220px)]'>
				<VariantTable
					columns={VARIANT_COLUMNS}
					data={auditList}
					customRowData={customRowData}
					isLoading={isLoading}
				/>
			</div>
		</div>
	);
}

// ─── Redux ────────────────────────────────────────────────────────────────────

const mapStateToProps = (state) => ({
	storesList: state.form.STORES?.data?.LIST ?? [],
});

export default connect(mapStateToProps)(MyDepartmentsAudit);
